/* component-modal-dialog.js — <modal-dialog>
   Thin wrapper around the native <dialog> element. Shared; size guide,
   comparison, and any section can reuse it.

   Markup:
     <modal-dialog>
       <button data-modal-open>Open</button>   (optional inline trigger)
       <dialog data-modal>
         <button data-modal-close aria-label="...">×</button>
         ...content...
       </dialog>
     </modal-dialog>

   External triggers: any element with [data-modal-target="<id>"] where <id>
   matches this element's id attribute will open it on click.

   Emits no PubSub — purely local UI. Restores focus + scroll on close. */
class ModalDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-modal]');
    if (!this.dialog) return;
    this._openers = this.querySelectorAll('[data-modal-open]');
    this._closers = this.querySelectorAll('[data-modal-close]');
    this._lastFocus = null;

    this._onOpen = () => this.open();
    this._onClose = () => this.close();
    this._openers.forEach((b) => b.addEventListener('click', this._onOpen));
    this._closers.forEach((b) => b.addEventListener('click', this._onClose));

    // Backdrop click closes (native dialog fires click on the ::backdrop area too).
    this._onBackdrop = (e) => { if (e.target === this.dialog) this.close(); };
    this.dialog.addEventListener('click', this._onBackdrop);

    // External triggers by id.
    if (this.id) {
      this._extTriggers = document.querySelectorAll('[data-modal-target="' + CSS.escape(this.id) + '"]');
      this._extTriggers.forEach((b) => b.addEventListener('click', this._onOpen));
    }
  }

  disconnectedCallback() {
    if (this._openers) this._openers.forEach((b) => b.removeEventListener('click', this._onOpen));
    if (this._closers) this._closers.forEach((b) => b.removeEventListener('click', this._onClose));
    if (this._extTriggers) this._extTriggers.forEach((b) => b.removeEventListener('click', this._onOpen));
    if (this.dialog) this.dialog.removeEventListener('click', this._onBackdrop);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this._lastFocus = document.activeElement;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.documentElement.style.overflow = 'hidden';
    const focusable = this.dialog.querySelector('[autofocus], button, [href], input, select, textarea');
    if (focusable) focusable.focus();
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function' && this.dialog.open) {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
    document.documentElement.style.overflow = '';
    if (this._lastFocus && typeof this._lastFocus.focus === 'function') this._lastFocus.focus();
  }
}

if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);
